import React from "react";
import { Box, Button, Divider, Stack, Typography } from "@mui/material";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import { useTranslation } from "react-i18next";
import { PresentationAssistantNotes } from "../../../models/PresentationDraft";
import { zoullooColors } from "../../../theme";

interface AssistantNotesPanelProps {
	readonly notes?: PresentationAssistantNotes | null;
	readonly onShowTranscript: () => void;
}

const NotesSection: React.FC<{ readonly title: string; readonly children: React.ReactNode }> = ({ title, children }) => (
	<Box>
		<Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
			{title}
		</Typography>
		{children}
	</Box>
);

const EmptyLine: React.FC<{ readonly text: string }> = ({ text }) => (
	<Typography variant="body2" color="text.secondary" fontStyle="italic">
		{text}
	</Typography>
);

const BulletList: React.FC<{ readonly items: string[] }> = ({ items }) => (
	<Box component="ul" sx={{ m: 0, pl: 2.5, display: "flex", flexDirection: "column", gap: 0.75 }}>
		{items.map(item => (
			<Typography component="li" variant="body2" key={item}>
				{item}
			</Typography>
		))}
	</Box>
);

const AssistantNotesPanel: React.FC<AssistantNotesPanelProps> = ({ notes, onShowTranscript }) => {
	const { t } = useTranslation();
	const pointsToVerify = notes?.pointsToVerify ?? [];
	const dimensions = notes?.dimensions ?? [];
	const choices = notes?.choices ?? [];

	return (
		<Stack
			spacing={2.5}
			divider={<Divider flexItem />}
			sx={{
				p: 2.5,
				borderRadius: "12px",
				border: `1px solid ${zoullooColors.grey[300]}`,
				backgroundColor: zoullooColors.background.default
			}}
		>
			<NotesSection title={t("presentationDraft.notes.pointsToVerify", { count: pointsToVerify.length })}>
				{pointsToVerify.length > 0 ? (
					<BulletList items={pointsToVerify} />
				) : (
					<EmptyLine text={t("presentationDraft.notes.nothingToVerify")} />
				)}
			</NotesSection>

			<NotesSection title={t("presentationDraft.notes.dimensions")}>
				{dimensions.length > 0 ? (
					<Stack spacing={1.5}>
						{dimensions.map(dimension => (
							<Box key={`${dimension.dimension}-${dimension.location}`}>
								<Typography variant="body2" fontWeight={600}>
									{dimension.dimension}
								</Typography>
								<Typography variant="caption" color="text.secondary" component="p">
									{t("presentationDraft.notes.location", { location: dimension.location })}
								</Typography>
								<Typography variant="caption" color="text.secondary" component="p">
									{t("presentationDraft.notes.clientBenefit", { benefit: dimension.clientBenefit })}
								</Typography>
							</Box>
						))}
					</Stack>
				) : (
					<EmptyLine text={t("presentationDraft.notes.noDimensions")} />
				)}
			</NotesSection>

			<NotesSection title={t("presentationDraft.notes.choices")}>
				{choices.length > 0 ? <BulletList items={choices} /> : <EmptyLine text={t("presentationDraft.notes.noChoices")} />}
			</NotesSection>

			<Button
				variant="text"
				color="secondary"
				startIcon={<ArticleOutlinedIcon />}
				onClick={onShowTranscript}
				sx={{ alignSelf: "flex-start" }}
			>
				{t("presentationDraft.notes.showTranscript")}
			</Button>
		</Stack>
	);
};

export default AssistantNotesPanel;
